import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Lock, Shield } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { api } from '../services/api';
import { normalizeDisplayCurrency } from '../constants/currencies';

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  order_id: string;
  name: string;
  description?: string;
  handler: (response: RazorpaySuccessResponse) => void;
  prefill?: { name?: string; email?: string };
  theme?: { color?: string };
}

interface RazorpaySuccessResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayInstance {
  open: () => void;
  on: (event: string, handler: (response: unknown) => void) => void;
}

interface RazorpayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  bookingId: string;
  amount: number;
  currency: string;
  courseName?: string;
}

const RazorpayModal = ({
  isOpen,
  onClose,
  onConfirm,
  bookingId,
  amount,
  currency,
  courseName,
}: RazorpayModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptReady, setIsScriptReady] = useState(false);
  const paymentInProgressRef = useRef(false);

  useBodyScrollLock(isOpen);

  const formatCurrency = (value: number, curr: string) => {
    const code = normalizeDisplayCurrency(curr);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const loadRazorpayScript = () => {
    if (window.Razorpay) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[src^="https://checkout.razorpay.com"]');
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Razorpay'));
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    loadRazorpayScript()
      .then(() => setIsScriptReady(true))
      .catch((err) => setError(err?.message || 'Failed to load Razorpay'));
  }, [isOpen]);

  const handlePayWithRazorpay = async () => {
    if (paymentInProgressRef.current) return;
    setError(null);
    setIsLoading(true);
    paymentInProgressRef.current = true;

    try {
      const createRes = await api.post('/v1/payment/razorpay/orders', {
        bookingId,
        amount,
        currency_code: currency || 'INR',
      });

      if (!createRes.data?.success) {
        throw new Error(createRes.data?.message || 'Failed to create order');
      }

      const { orderId, razorpayOrderId, keyId, amountInSubunits } = createRes.data.data;

      if (!keyId) {
        throw new Error('Razorpay is not configured');
      }

      const options: RazorpayOptions = {
        key: keyId,
        amount: amountInSubunits,
        currency: currency || 'INR',
        order_id: razorpayOrderId,
        name: 'BookMyYogaTTC',
        description: courseName || 'Yoga Teacher Training Course Booking',
        theme: { color: '#FF6B35' },
        handler: async (response: RazorpaySuccessResponse) => {
          try {
            const verifyRes = await api.post(`/v1/payment/razorpay/orders/${orderId}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (!verifyRes.data?.success) {
              throw new Error(verifyRes.data?.message || 'Payment verification failed');
            }

            await onConfirm();
            onClose();
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Payment verification failed';
            setError(msg);
          } finally {
            setIsLoading(false);
            paymentInProgressRef.current = false;
          }
        },
      };

      const razorpay = new window.Razorpay!(options);
      razorpay.on('payment.failed', (response: unknown) => {
        const err = (response as { error?: { description?: string } })?.error;
        setError(err?.description || 'Payment failed');
        setIsLoading(false);
        paymentInProgressRef.current = false;
      });
      razorpay.open();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to initiate payment';
      setError(msg);
    } finally {
      setIsLoading(false);
      paymentInProgressRef.current = false;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 20000,
            touchAction: 'none',
            overflow: 'hidden',
            overscrollBehavior: 'none',
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
            style={{
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <h2 className="text-5 font-semibold">Pay with Razorpay</h2>
                    <p className="text-2 text-white/80">Secure payment via UPI, Cards & more</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6" style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
              {courseName && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-2 text-gray-600 mb-1">Course</p>
                  <p className="font-semibold text-gray-900">{courseName}</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 border-2 border-primary/20">
                <div className="text-center">
                  <p className="text-2 text-gray-600 mb-2">Total Amount</p>
                  <p className="text-5 font-semibold text-primary mb-1">
                    {formatCurrency(amount, currency)}
                  </p>
                  <p className="text-1 text-gray-500">Initial booking amount</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-4 bg-white">
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="h-5 w-5 text-gray-500" />
                    <p className="text-2 font-semibold text-gray-700">Pay securely with Razorpay</p>
                  </div>
                  <p className="text-2 text-gray-600 mb-4">
                    Supports UPI, Credit/Debit Cards, Net Banking & Wallets
                  </p>
                  <button
                    type="button"
                    onClick={handlePayWithRazorpay}
                    disabled={!isScriptReady || isLoading}
                    className="w-full py-3 px-6 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#0C2451' }}
                  >
                    {isLoading ? (
                      <span className="animate-pulse">Opening Razorpay...</span>
                    ) : (
                      <>
                        <span className="font-bold">R</span>
                        Pay with Razorpay
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-center gap-4 text-1 text-gray-600">
                  <div className="flex items-center gap-1">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Lock className="h-4 w-4 text-green-600" />
                    <span>256-bit Encryption</span>
                  </div>
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3"
                >
                  <X className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <p className="text-2 text-red-800">{error}</p>
                </motion.div>
              )}
            </div>

            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <button
                onClick={onClose}
                className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RazorpayModal;
